import React, { useState, useRef, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { Dish, Extra, OrderItem } from '../types';
import { useOrder } from '../context/OrderContext';
import { AppContext } from '../context/AppContext';
import { useData } from '../context/DataContext';
import { StarIcon } from './icons';

interface DishDetailModalProps {
    dish: Dish;
    restaurantName: string;
    restaurantId: string;
    onClose: () => void;
    globalDiscount?: number;
    restaurantWilaya?: string;
    restaurantRating?: number;
    minimal?: boolean;
    isOffline?: boolean;
    editModeParams?: {
        items: OrderItem[];
        onUpdateItems: (items: OrderItem[]) => void;
    };
}

const DishDetailModal: React.FC<DishDetailModalProps> = ({
    dish,
    restaurantName,
    restaurantId,
    onClose,
    globalDiscount = 0,
    restaurantWilaya,
    restaurantRating,
    minimal = false,
    isOffline = false,
    editModeParams
}) => {
    const { addToOrder, updateQuantity, updateItemExtras, cartOrders } = useOrder();
    const { setView, setIsCartOpen } = useContext(AppContext);
    const { getRestaurantById } = useData();

    const restaurant = getRestaurantById(restaurantId);
    const globalExtras = restaurant?.extras || [];

    // Combine dish-specific extras and restaurant-wide extras
    const allExtras = [
        ...(dish.extras || []),
        ...globalExtras.filter((ge: any) => !(dish.extras || []).some((de: any) => de.id === ge.id))
    ];

    // Reset scroll to top on mount
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, []);

    const effectiveDiscount = dish.discount && dish.discount > 0 ? dish.discount : globalDiscount;
    const hasDiscount = effectiveDiscount > 0;
    const finalPrice = hasDiscount ? Math.round(dish.price * (1 - effectiveDiscount / 100)) : dish.price;

    const cartItem = editModeParams 
        ? editModeParams.items.find((item: OrderItem) => (item.type === 'dish' ? item.dish?.id : item.pack?.id) === dish.id)
        : cartOrders
            .find((o: any) => o.restaurantId === restaurantId)
            ?.items.find((item: OrderItem) => (item.type === 'dish' ? item.dish?.id : item.pack?.id) === dish.id);
    const quantity = cartItem ? cartItem.quantity : 0;
    const [selectedExtras, setSelectedExtras] = useState<Extra[]>(cartItem?.selectedExtras || []);

    const extraPriceTotal = selectedExtras.reduce((sum, extra) => sum + extra.price, 0);
    const totalPricePerUnit = finalPrice + extraPriceTotal;

    const incrementExtra = (extra: Extra) => {
        const newExtras = [...selectedExtras, extra];
        setSelectedExtras(newExtras);
        if (quantity > 0) {
            if (editModeParams) {
                const newItems = editModeParams.items.map(item => 
                    (item.type === 'dish' ? item.dish?.id : item.pack?.id) === dish.id ? { ...item, selectedExtras: newExtras } : item
                );
                editModeParams.onUpdateItems(newItems);
            } else {
                updateItemExtras(dish.id, restaurantId, newExtras);
            }
        }
    };

    const decrementExtra = (extra: Extra) => {
        const index = selectedExtras.findLastIndex(e => e.id === extra.id);
        if (index > -1) {
            const newExtras = [...selectedExtras];
            newExtras.splice(index, 1);
            setSelectedExtras(newExtras);
            if (quantity > 0) {
                if (editModeParams) {
                    const newItems = editModeParams.items.map(item => 
                        (item.type === 'dish' ? item.dish?.id : item.pack?.id) === dish.id ? { ...item, selectedExtras: newExtras } : item
                    );
                    editModeParams.onUpdateItems(newItems);
                } else {
                    updateItemExtras(dish.id, restaurantId, newExtras);
                }
            }
        }
    };

    // --- Gesture Logic ---
    const [dragY, setDragY] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const startY = useRef(0);
    const scrollRef = useRef<HTMLDivElement>(null);

    const handleTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        startY.current = clientY;
        setIsDragging(true);
    };

    const handleTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
        if (!isDragging) return;
        const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        const deltaY = clientY - startY.current;
        if (deltaY > 0) {
            setDragY(deltaY);
        }
    };

    const handleTouchEnd = () => {
        if (dragY > 120) {
            onClose();
        } else {
            setDragY(0);
        }
        setIsDragging(false);
    };

    const increment = () => {
        if (quantity > 0) {
            if (editModeParams) {
                const newItems = editModeParams.items.map(item => 
                    (item.type === 'dish' ? item.dish?.id : item.pack?.id) === dish.id ? { ...item, quantity: quantity + 1 } : item
                );
                editModeParams.onUpdateItems(newItems);
            } else {
                updateQuantity(dish.id, quantity + 1, restaurantId);
            }
        } else {
            if (editModeParams) {
                const newItem: OrderItem = { type: 'dish', dish: { ...dish, price: finalPrice }, quantity: 1, restaurantName, restaurantId, selectedExtras };
                editModeParams.onUpdateItems([...editModeParams.items, newItem]);
            } else {
                addToOrder({ ...dish, price: finalPrice }, restaurantName, restaurantId, selectedExtras);
            }
        }
    };

    const decrement = () => {
        if (quantity > 0) {
            if (editModeParams) {
                if (quantity === 1) {
                    editModeParams.onUpdateItems(editModeParams.items.filter(item => (item.type === 'dish' ? item.dish?.id : item.pack?.id) !== dish.id));
                } else {
                    const newItems = editModeParams.items.map(item => 
                        (item.type === 'dish' ? item.dish?.id : item.pack?.id) === dish.id ? { ...item, quantity: quantity - 1 } : item
                    );
                    editModeParams.onUpdateItems(newItems);
                }
            } else {
                updateQuantity(dish.id, quantity - 1, restaurantId);
            }
        }
    };

    const modalContent = (
        <div
            className="fixed inset-0 z-[1100] flex items-center justify-center pointer-events-none p-4"
            style={{ backgroundColor: `rgba(0,0,0,${Math.max(0, 0.6 - dragY / 500)})` }}
        >
            <div
                className="absolute inset-0 pointer-events-auto"
                onClick={onClose}
            />

            <div
                className={`${minimal ? 'mt-auto' : ''} mx-auto max-w-lg w-full bg-white rounded-[2.5rem] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.5)] overflow-hidden pointer-events-auto flex flex-col transition-all duration-300 animate-scale-in`}
                style={{
                    transform: `translateY(${dragY}px)`,
                    maxHeight: minimal ? '80vh' : '90vh'
                }}
            >
                {/* Content Area */}
                <div
                    ref={scrollRef}
                    className={`flex-1 overflow-y-auto overscroll-y-auto touch-pan-y ${minimal ? 'pb-52' : 'pb-48'}`}
                    style={{ WebkitOverflowScrolling: 'touch' }}
                >
                    {!minimal && (
                        <div className="relative h-80 w-full">
                            <img
                                src={dish.image}
                                alt={dish.name}
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-transparent" />
                            <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white/60 to-transparent" />

                            <div
                                className="absolute top-0 left-0 right-0 flex justify-center py-5 z-20 cursor-grab active:cursor-grabbing touch-none"
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                                onMouseDown={handleTouchStart}
                            >
                                <div className="w-14 h-1.5 bg-white/60 backdrop-blur-md rounded-full shadow-sm" />
                            </div>

                            {hasDiscount && (
                                <div className="absolute top-10 left-6 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-black shadow-lg z-20">
                                    -{effectiveDiscount}%
                                </div>
                            )}

                            <button
                                onClick={onClose}
                                className="absolute top-8 right-6 bg-black/20 backdrop-blur-md text-white rounded-full p-2 hover:bg-black/40 transition-all z-20 shadow-lg border border-white/10"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    )}

                    {minimal && (
                        <div className="flex flex-col">
                            <div className="flex justify-center pt-4 pb-2">
                                <div className="w-12 h-1 bg-gray-200 rounded-full" />
                            </div>
                            <div className="px-6 flex justify-between items-center py-2 border-b border-gray-50">
                                <h2 className="text-xl font-black text-gray-900 tracking-tight">{dish.name}</h2>
                                <button onClick={onClose} className="p-2 bg-gray-100 rounded-full text-gray-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )}

                    <div className={`${minimal ? 'px-6 pt-4' : 'px-8 pt-6 relative z-10 bg-white'}`}>
                        {!minimal && (
                            <>
                                {(restaurantWilaya || restaurantRating) && (
                                    <div className="flex items-center gap-3 mb-3 bg-white/90 backdrop-blur-sm self-start inline-flex px-3 py-1.5 rounded-full shadow-sm border border-gray-100">
                                        <span className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                                            </svg>
                                            {restaurantName}
                                        </span>
                                        {(restaurantWilaya || restaurantRating) && <span className="w-1 h-1 bg-gray-300 rounded-full" />}
                                        {restaurantWilaya && <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{restaurantWilaya}</span>}
                                        {restaurantRating && (
                                            <span className="flex items-center text-[10px] font-bold text-gray-700">
                                                <StarIcon className="w-3 h-3 text-yellow-400 mr-0.5" />
                                                {restaurantRating}
                                            </span>
                                        )}
                                    </div>
                                )}

                                <div className="flex justify-between items-start mb-6">
                                    <h2 className="text-3xl font-black text-gray-900 leading-tight flex-1 mr-6 tracking-tight">
                                        {dish.name}
                                    </h2>
                                </div>

                                <p className="text-gray-600 text-base leading-relaxed mb-4 font-medium">
                                    {dish.description}
                                </p>

                                {dish.ingredients && dish.ingredients.length > 0 && (
                                    <div className="py-4 border-t border-gray-100 mt-0">
                                        <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Ingrédients</h3>
                                        <p className="text-gray-700 text-sm font-bold leading-relaxed">
                                            {dish.ingredients.join(' • ')}
                                        </p>
                                    </div>
                                )}
                            </>
                        )}

                        {allExtras.length > 0 && (
                            <div className={`${minimal ? '' : 'py-6 border-t border-gray-100'}`}>
                                <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Extras & Suppléments</h3>
                                <div className="space-y-3">
                                    {allExtras.map((extra) => {
                                        const extraCount = selectedExtras.filter(e => e.id === extra.id).length;
                                        const isGlobal = globalExtras.some(ge => ge.id === extra.id);
                                        return (
                                            <div
                                                key={extra.id}
                                                className={`w-full flex items-center justify-between p-4 rounded-2xl border-2 transition-all ${extraCount > 0
                                                    ? 'border-red-500 bg-red-50 shadow-md shadow-red-100'
                                                    : 'border-gray-100 bg-gray-50'
                                                    }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div>
                                                        <span className={`font-bold block text-left ${extraCount > 0 ? 'text-red-900' : 'text-gray-700'}`}>
                                                            {extra.name}
                                                        </span>
                                                        <div className="flex items-center gap-2">
                                                            <span className={`text-xs font-black ${extraCount > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                                                                +{extra.price} DA
                                                            </span>
                                                            {isGlobal && (
                                                                <span className="text-[10px] font-bold text-green-600 uppercase tracking-tighter">Restaurant</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center bg-white rounded-xl border border-gray-200 p-1 shadow-sm">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); decrementExtra(extra); }}
                                                        className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold transition-all ${extraCount > 0 ? 'text-red-600 hover:bg-red-50' : 'text-gray-300'}`}
                                                        disabled={extraCount === 0}
                                                    >
                                                        -
                                                    </button>
                                                    <span className={`px-2 font-black text-sm min-w-[1.5rem] text-center ${extraCount > 0 ? 'text-gray-900' : 'text-gray-400'}`}>
                                                        {extraCount}
                                                    </span>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); incrementExtra(extra); }}
                                                        className="w-8 h-8 rounded-lg flex items-center justify-center text-red-600 font-bold hover:bg-red-50 transition-all"
                                                    >
                                                        +
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Bottom Action Bar */}
                <div className={`p-6 border-t border-gray-100 bg-white z-20 absolute bottom-0 left-0 right-0 ${minimal ? 'pb-safe-bottom' : ''}`}>
                    <div className="flex flex-col gap-4">
                        {!minimal && !isOffline && (
                            <div className="flex items-center bg-gray-100 rounded-2xl p-1.5 h-14 justify-between px-3">
                                <button
                                    onClick={decrement}
                                    className="w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center text-red-600 font-bold text-xl active:scale-95 transition-all"
                                >
                                    -
                                </button>
                                <span className="font-black text-xl text-gray-900">{quantity}</span>
                                <button
                                    onClick={increment}
                                    className="w-10 h-10 bg-red-600 rounded-xl shadow-sm flex items-center justify-center text-white font-bold text-xl active:scale-95 transition-all"
                                >
                                    +
                                </button>
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={onClose}
                                className="flex-1 bg-gray-900 text-white font-black py-4 rounded-xl active:scale-95 transition-all uppercase tracking-widest text-xs"
                            >
                                {minimal ? 'Terminer' : (isOffline ? 'Fermer' : 'Continuer')}
                            </button>
                            
                            {!isOffline && (
                                <button
                                    onClick={() => {
                                        onClose();
                                        if (!editModeParams) {
                                            // Petit délai pour laisser le temps à la modal de se fermer
                                            // et au système de navigation de se stabiliser
                                            setTimeout(() => setIsCartOpen(true), 150);
                                        }
                                    }}
                                    className={`flex-1 ${editModeParams ? 'bg-gray-800' : 'bg-red-600'} text-white font-black py-4 rounded-xl shadow-xl shadow-red-100 active:scale-95 transition-all uppercase tracking-widest text-xs`}
                                >
                                    {editModeParams ? "Terminer" : "Voir Panier"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default DishDetailModal;
